import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { OpsApiError, opsApiRequest } from '../api/client.ts';

/**
 * Ops-console session context.
 *
 * Single source of truth for "am I logged in, and as whom". The provider
 * runs one `GET /v1/auth/me` at mount to hydrate. Login / logout flows
 * update the context directly — they do NOT trigger a second /me round-
 * trip.
 *
 * The cookie itself lives in the browser jar; this context only mirrors
 * what the server told us. `clearSession` nulls the mirror AFTER the
 * caller has successfully POSTed /logout.
 *
 * Status values:
 *   - 'loading'      — initial probe pending
 *   - 'authenticated'— /me returned 200, `user` populated
 *   - 'unauthenticated' — /me returned 401 or no session
 */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export interface SessionUser {
  opsUserId: string;
  email: string;
  roles: readonly string[];
}

export interface SessionContextValue {
  status: SessionStatus;
  user: SessionUser | null;
  setUser: (user: SessionUser) => void;
  clearUser: () => void;
  refresh: () => Promise<void>;
  /**
   * The fetch implementation used for every ops-api call made by
   * consumers of this provider. Production callers get `window.fetch`;
   * tests inject a stub.
   */
  fetchImpl: typeof fetch;
}

const SessionContext = createContext<SessionContextValue | null>(null);

interface MeResponse {
  ops_user_id: string;
  issued_at: number;
  expires_at: number;
}

interface ProviderProps {
  children: ReactNode;
  /**
   * Injection point for tests. Production callers leave undefined and
   * the provider falls back to `window.fetch`.
   */
  fetchImpl?: typeof fetch;
}

export function SessionProvider({ children, fetchImpl }: ProviderProps): JSX.Element {
  const resolvedFetch: typeof fetch = useMemo(
    () => fetchImpl ?? ((...args) => fetch(...args)),
    [fetchImpl],
  );
  const [status, setStatus] = useState<SessionStatus>('loading');
  const [user, setUserState] = useState<SessionUser | null>(null);

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      const me = await opsApiRequest<MeResponse>('/v1/auth/me', {}, resolvedFetch);
      // `/v1/auth/me` only returns the opaque ops_user_id today (see
      // ops-api route docs). Email + roles are cached from the /login
      // response; a background refresh cannot repopulate them until
      // platform-api adds an /internal/ops/get-user lookup. For now the
      // context surfaces whatever the caller last set, falling back to
      // an "unknown" placeholder so the guard can still render.
      setUserState((prev) => {
        if (prev !== null && prev.opsUserId === me.ops_user_id) {
          return prev;
        }
        return { opsUserId: me.ops_user_id, email: '', roles: [] };
      });
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof OpsApiError && err.status === 401) {
        setUserState(null);
        setStatus('unauthenticated');
        return;
      }
      // Network / 5xx errors: treat as unauthenticated in the UI so
      // RequireAuth redirects to /login. Surfacing a "service down"
      // banner is a Wave 5 polish item.
      setUserState(null);
      setStatus('unauthenticated');
    }
  }, [resolvedFetch]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setUser = useCallback((next: SessionUser) => {
    setUserState(next);
    setStatus('authenticated');
  }, []);

  const clearUser = useCallback(() => {
    setUserState(null);
    setStatus('unauthenticated');
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ status, user, setUser, clearUser, refresh, fetchImpl: resolvedFetch }),
    [status, user, setUser, clearUser, refresh, resolvedFetch],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (ctx === null) {
    throw new Error('useSession must be used inside <SessionProvider>');
  }
  return ctx;
}
